/**
 * Genesis — the Marketing Department Builder.
 *
 * Single-page orchestrator for the 8-phase adoption experience.
 * Each phase is a small component; the page owns session state
 * + phase advancement + resume-on-refresh.
 *
 * Design intent: the customer should never feel like they are on a
 * "step 3 of 8" form. The shell is intentionally minimal — no
 * progress bar, no back button, no navigation chrome. Just a single
 * unhurried focus target per phase.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  advancePhase, completeSession, recordAnalytics,
  saveAnswers, saveDiscovery, selectLogo, startSession, transform,
  type BriefingPayload,
  type GenesisPhase, type GenesisSession, type IntellyAnswers,
} from "@/entities/genesis";

import { WelcomeStep } from "./steps/WelcomeStep";
import { DiscoveryStep } from "./steps/DiscoveryStep";
import { DemoDepartmentStep } from "./steps/DemoDepartmentStep";
import { MakeThisMineStep } from "./steps/MakeThisMineStep";
import { IntellyConversationStep } from "./steps/IntellyConversationStep";
import { LogoStep } from "./steps/LogoStep";
import { TransformationStep } from "./steps/TransformationStep";
import { BriefingStep } from "./steps/BriefingStep";
import { GenesisShell } from "./components/GenesisShell";
import { listWorkspaces } from "@/entities/workspace/api";
import { useWorkspaceStore } from "@/app/store/workspace-store";

export default function GenesisPage(): JSX.Element {
  const nav = useNavigate();
  const qc = useQueryClient();
  const setWorkspaces = useWorkspaceStore((state) => state.setWorkspaces);
  const selectWorkspace = useWorkspaceStore((state) => state.selectWorkspace);

  const startQ = useQuery({
    queryKey: ["genesis", "session"],
    queryFn: startSession,
    staleTime: Infinity,
    retry: 1,
  });
  const session: GenesisSession | undefined = startQ.data;

  // Local mirror so phase transitions feel instant even before the server
  // acknowledges. Server truth wins on next refetch.
  const [phase, setPhase] = useState<GenesisPhase | null>(null);
  useEffect(() => {
    if (session && phase === null) setPhase(session.current_phase);
  }, [session, phase]);

  const advance = useMutation({
    mutationFn: async (to: GenesisPhase) => {
      if (!session) return undefined;
      const next = await advancePhase(session._id, to);
      return next;
    },
    onSuccess: (data, to) => {
      if (data) qc.setQueryData(["genesis", "session"], data);
      setPhase(to);
    },
  });

  const goto = (next: GenesisPhase) => {
    setPhase(next);            // optimistic
    advance.mutate(next);      // persist
  };

  const analyticsMut = useMutation({
    mutationFn: async (patch: Parameters<typeof recordAnalytics>[1]) => {
      if (!session) return;
      await recordAnalytics(session._id, patch);
    },
  });

  const shell = useMemo(() => phase ?? "welcome", [phase]);

  if (startQ.isPending || !session) {
    return (
      <GenesisShell phase="welcome">
        <div className="flex min-h-[60vh] items-center justify-center text-fog/70">
          Preparing your marketing department…
        </div>
      </GenesisShell>
    );
  }

  return (
    <GenesisShell phase={shell as GenesisPhase}>
      {shell === "welcome" && (
        <WelcomeStep onNext={() => goto("discovery")} />
      )}
      {shell === "discovery" && (
        <DiscoveryStep
          session={session}
          onNext={async (industryKey, customDescription) => {
            const result = await saveDiscovery(session._id, {
              industry_key: industryKey,
              custom_description: customDescription,
            });
            qc.setQueryData(["genesis", "session"], result.session);
            goto("demo_department");
          }}
        />
      )}
      {shell === "demo_department" && (
        <DemoDepartmentStep
          session={session}
          onMakeItMine={() => {
            analyticsMut.mutate({
              time_to_make_this_mine_seconds: session.time_in_demo_seconds,
            });
            goto("make_this_mine");
          }}
          onTick={(sec) => analyticsMut.mutate({ time_in_demo_seconds: sec })}
        />
      )}
      {shell === "make_this_mine" && (
        <MakeThisMineStep
          onConfirm={() => goto("intelly_conversation")}
          onBack={() => goto("demo_department")}
        />
      )}
      {shell === "intelly_conversation" && (
        <IntellyConversationStep
          initial={session.answers}
          onBack={() => goto("make_this_mine")}
          onNext={async (answers: IntellyAnswers) => {
            const next = await saveAnswers(session._id, answers);
            qc.setQueryData(["genesis", "session"], next);
            const wantsLogo = answers.has_logo === false;
            goto(wantsLogo ? "logo" : "transformation");
          }}
        />
      )}
      {shell === "logo" && (
        <LogoStep
          sessionId={session._id}
          onSelect={async (conceptId) => {
            const next = await selectLogo(session._id, conceptId);
            qc.setQueryData(["genesis", "session"], next);
          }}
          onNext={() => goto("transformation")}
          onSkip={() => goto("transformation")}
          onBack={() => goto("intelly_conversation")}
        />
      )}
      {shell === "transformation" && (
        <TransformationStep
          onContinue={() => nav("/onboarding")}
          onReady={async () => {
            const briefing = await transform(session._id);
            const workspaces = await listWorkspaces();
            const created = workspaces.find((item) => item.id === briefing.workspace_id);
            if (!created) throw new Error("Your workspace was created but could not be opened yet.");
            setWorkspaces(workspaces);
            selectWorkspace(created.id);
            qc.setQueryData(["workspaces"], workspaces);
            qc.setQueryData(["genesis", "session"], {
              ...session, briefing, workspace_id: briefing.workspace_id,
            });
            goto("briefing");
          }}
        />
      )}
      {shell === "briefing" && session.briefing && (
        <BriefingStep
          briefing={session.briefing as BriefingPayload}
          onEnter={async () => {
            await completeSession(session._id);
            selectWorkspace(session.briefing!.workspace_id);
            nav("/");
          }}
        />
      )}
    </GenesisShell>
  );
}
